import { useEffect, useRef, useState } from "react";
import {
  addPayment,
  updateOrAddPayment,
  updatePaymentItem,
} from "../../firebase/firebase.payment";
import PaymentDropdown from "./PaymentDropdown";
import { toast } from "react-hot-toast";
import DatePicker from "react-datepicker";
import { DatePickerIcon } from "./DatePickerIcon";
import Spinner from "./Spinner";
import { useSelector } from "react-redux";
import { generateUuid } from "../../firebase/firebase.helpers";

const PaymentModal = ({
  data,
  modalState,
  modalSetter,
  allPayments,
  allPaymentsSetter,
  editModal,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isNewService, setIsNewService] = useState(true);
  const [dropSelection, setDropSelection] = useState(null);
  const [defaultValue, setDefaultValue] = useState(undefined);
  const [currentDebt, setCurrentDebt] = useState(0);
  const [invoice, setInvoice] = useState("");
  const [paymentReason, setPaymentReason] = useState("");
  const [paymentDescription, setPaymentDescription] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [serviceIsRunning, setServiceIsRunning] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const cancelButtonRef = useRef(undefined);
  const user = useSelector((state) => state.user?.userDetails);

  useEffect(() => {
    if (allPayments) {
      if (allPayments) {
        let serviceToCharge = allPayments.find((i) => i.hasBeenPaid !== true);
        setDropSelection(serviceToCharge);
        if (serviceToCharge !== undefined) {
          setDefaultValue(serviceToCharge);
        } else {
          setDefaultValue([]);
        }
      }
    }
  }, [allPayments]);

  // Initialize form state when editModal changes (edit mode)
  useEffect(() => {
    if (editModal?.id !== undefined) {
      // We're in edit mode - First clear all fields to avoid stale data
      setInvoice("");
      setPaymentReason("");
      setPaymentAmount("");
      setPaymentDescription("");
      setPaymentDate(new Date());

      // Check if it's a service (has reason) or a payment (has invoice)
      if (editModal.reason !== undefined) {
        // It's a service
        setIsNewService(true);
        setPaymentReason(editModal.reason || "");
        setPaymentAmount(
          editModal.price?.toString() || editModal.balance?.toString() || "",
        );
        setPaymentDescription(editModal.description || "");
        if (editModal.date) {
          const date =
            typeof editModal.date === "string"
              ? new Date(editModal.date)
              : editModal.date.toDate();
          setPaymentDate(date);
        }
      } else if (
        editModal.invoice !== undefined ||
        editModal.amount !== undefined
      ) {
        // It's a payment
        setIsNewService(true); // Keep as true for the form layout
        setInvoice(editModal.invoice || "");
        setPaymentAmount(
          editModal.amount?.toString() || editModal.price?.toString() || "",
        );
        setPaymentDescription(editModal.description || "");
        if (editModal.date) {
          const date =
            typeof editModal.date === "string"
              ? new Date(editModal.date)
              : editModal.date.toDate();
          setPaymentDate(date);
        }
      }
    } else if (data.id !== undefined) {
      // Not in edit mode, just opening modal for a patient
      // Reset to defaults
      setInvoice("");
      setPaymentAmount("");
      setPaymentDescription("");
      setPaymentReason("");
      setPaymentDate(new Date());
      setIsNewService(true);
    }
  }, [editModal, data]);

  useEffect(() => {
    if (dropSelection) {
      if (paymentAmount !== undefined && paymentAmount !== "") {
        setCurrentDebt(dropSelection.balance - paymentAmount);
      } else {
        setCurrentDebt(dropSelection.balance);
      }
    }
  }, [dropSelection, paymentAmount]);

  const handleChange = (e) => {
    // Extract the input value
    let value = e.target.value;
    // Remove non-numeric characters
    value = value.replace(/[^0-9]/g, "");

    // Allow empty value for editing purposes
    if (value === "") {
      setPaymentAmount("");
      return;
    }

    // Validate the value is a valid number
    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      return;
    }

    // For new services or edit mode, allow any positive value
    if (isNewService || editModal?.id !== undefined) {
      setPaymentAmount(value);
    } else {
      // For adding payments to existing services, check against balance
      if (dropSelection && numValue <= dropSelection.balance) {
        setPaymentAmount(value);
      } else if (dropSelection) {
        toast.error("El monto supera el balance a pagar.");
      } else {
        setPaymentAmount(value);
      }
    }
  };

  // Validation function to check if form is valid for submission
  const isFormValid = () => {
    // Check if amount is valid (not 0 or empty)
    if (!paymentAmount || paymentAmount === "0" || paymentAmount === "") {
      return false;
    }

    // In edit mode, check based on what type of item we're editing
    if (editModal?.id !== undefined) {
      // Editing a service (has reason field)
      if (editModal.reason !== undefined) {
        return paymentReason !== "";
      }
      // Editing a payment (has invoice field)
      else if (editModal.invoice !== undefined) {
        return invoice !== "";
      }
      // Has amount field only (also a payment)
      return true;
    }

    // Not in edit mode - creating new
    if (isNewService) {
      // Creating a new service - requires reason
      return paymentReason !== "";
    } else {
      // Adding payment to existing service - requires invoice
      return invoice !== "";
    }
  };

  const updatePaymentHandler = async () => {
    setServiceIsRunning(true);

    try {
      let paymentAmountHandler =
        typeof paymentAmount === "string"
          ? JSON.parse(paymentAmount)
          : paymentAmount;

      // Case 1: Adding a payment to an existing service
      if (!isNewService && editModal?.id === undefined) {
        await addPayment(
          dropSelection.id,
          dropSelection,
          {
            amount: paymentAmountHandler,
            invoice: invoice,
            date: paymentDate,
            id: generateUuid(),
            description: paymentDescription,
          },
          modalSetter,
          allPaymentsSetter,
          setServiceIsRunning,
        );
        allPaymentsSetter(false);
      }
      // Case 2: Creating a new service
      else if (isNewService && editModal?.id === undefined) {
        await updateOrAddPayment(
          data.id,
          { id: 0 },
          {
            balance: paymentAmountHandler,
            date: new Date(paymentDate).toISOString(),
            hasBeenPaid: false,
            price: paymentAmountHandler,
            payments: [],
            id: "",
            reason: paymentReason,
            description: paymentDescription ? paymentDescription : "",
            praxisId: user.praxisId,
            praxisName: data.praxisName,
            patientId: data.id,
            patientName: data.personalDetails.name,
            createdBy: user.name,
            createdById: user.id,
          },
          modalSetter,
          allPaymentsSetter,
          setServiceIsRunning,
        );
        allPaymentsSetter(false);
      }
      // Case 3: Editing an existing service or payment
      else if (editModal?.id !== undefined) {
        const updatedItem = { ...editModal };

        // Update common fields - use current state values
        updatedItem.description = paymentDescription;
        updatedItem.date = new Date(paymentDate).toISOString();

        // Update service-specific fields (has reason field)
        if (updatedItem.reason !== undefined) {
          updatedItem.reason = paymentReason;
          updatedItem.price = paymentAmountHandler;

          // Recalculate balance if there are payments
          if (updatedItem.payments?.length > 0) {
            const paidAmount = updatedItem.payments.reduce(
              (sum, payment) => sum + payment.amount,
              0,
            );
            updatedItem.balance = updatedItem.price - paidAmount;
          } else {
            updatedItem.balance = updatedItem.price;
          }
        }

        // Update payment-specific fields (has invoice or amount field)
        if (updatedItem.invoice !== undefined) {
          updatedItem.invoice = invoice;
        }

        if (updatedItem.amount !== undefined) {
          updatedItem.amount = paymentAmountHandler;
        }

        await updatePaymentItem(updatedItem);
        setServiceIsRunning(false);
        allPaymentsSetter(false);
        modalSetter(false);
      }
    } catch (error) {
      console.error("Error in payment handler:", error);
      toast.error("Error al guardar. Por favor intenta de nuevo.");
      setServiceIsRunning(false);
    }
  };

  return (
    <>
      <div
        className="flex items-center justify-center overflow-x-hidden overflow-y-auto fixed inset-0 outline-none focus:outline-none p-4 sm:p-0"
        style={{ zIndex: "100" }}
      >
        <div className="relative w-full h-full sm:h-auto sm:my-6 mx-auto lg:w-50vw md:w-70vw sm:w-90vw max-w-3xl sm:max-h-[90vh]">
          {/*content*/}
          <div className="border-0 rounded-xl shadow-2xl relative flex flex-col w-full h-full sm:h-auto bg-white outline-none focus:outline-none sm:max-h-[90vh]">
            {/*header*/}
            {
              <>
                <div className="flex items-center justify-between p-6 border-b border-gray-200 rounded-t bg-gradient-to-r from-gray-50 to-gray-100 flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <div className="bg-emerald-500 p-2 rounded-lg">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="w-6 h-6 text-white"
                      >
                        {editModal?.id === undefined ? (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        ) : (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
                          />
                        )}
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">
                        {editModal?.id === undefined
                          ? "Pagos y Servicios"
                          : "Editor de Pagos y Servicios"}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {editModal?.id === undefined
                          ? "Registra un nuevo pago o servicio"
                          : "Modifica la información del registro"}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="w-full px-6 pt-6 pb-6 overflow-y-auto flex-1">
                  {editModal?.id === undefined && (
                    <div className="mb-4">
                      <label className="text-sm font-medium text-gray-700 mb-3 block">
                        <span className="text-red-500 mr-1">*</span>
                        Tipo de Operación
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          className={`group relative flex flex-col items-center justify-center p-6 border-2 rounded-xl transition-all duration-200 ${
                            isNewService
                              ? "border-emerald-500 bg-emerald-50 shadow-md"
                              : "border-gray-200 hover:border-emerald-300 hover:bg-gray-50"
                          }`}
                          onClick={() => setIsNewService(true)}
                        >
                          <div
                            className={`p-3 rounded-lg mb-3 transition-colors ${
                              isNewService
                                ? "bg-emerald-500 text-white"
                                : "bg-gray-100 text-gray-600 group-hover:bg-emerald-100 group-hover:text-emerald-600"
                            }`}
                          >
                            <svg
                              viewBox="0 0 64 64"
                              fill="none"
                              stroke="currentColor"
                              className="w-8 h-8"
                              strokeWidth={2}
                            >
                              <path
                                strokeMiterlimit={10}
                                d="M32 27v18M41 36H23M23 8H10v55h44V8H41"
                              />
                              <path
                                strokeMiterlimit={10}
                                d="M36 5V1h-8v4h-4l-2 8h20l-2-8z"
                              />
                            </svg>
                          </div>
                          <span
                            className={`font-semibold text-sm ${
                              isNewService
                                ? "text-emerald-700"
                                : "text-gray-700"
                            }`}
                          >
                            Nuevo Servicio
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            Crear servicio nuevo
                          </span>
                          {isNewService && (
                            <div className="absolute top-3 right-3">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-5 h-5 text-emerald-500"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </button>

                        <button
                          type="button"
                          className={`group relative flex flex-col items-center justify-center p-6 border-2 rounded-xl transition-all duration-200 ${
                            !isNewService
                              ? "border-blue-500 bg-blue-50 shadow-md"
                              : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                          }`}
                          onClick={() => setIsNewService(false)}
                        >
                          <div
                            className={`p-3 rounded-lg mb-3 transition-colors ${
                              !isNewService
                                ? "bg-blue-500 text-white"
                                : "bg-gray-100 text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-600"
                            }`}
                          >
                            <svg
                              viewBox="0 0 64 64"
                              fill="none"
                              stroke="currentColor"
                              className="w-8 h-8"
                              strokeWidth={2}
                            >
                              <path
                                strokeMiterlimit={10}
                                d="M23 8H10v55h44V8H41"
                              />
                              <path
                                strokeMiterlimit={10}
                                d="M36 5V1h-8v4h-4l-2 8h20l-2-8z"
                              />
                              <path
                                strokeLinejoin="round"
                                strokeMiterlimit={10}
                                d="M32 41l-6.125 4L28 38l-6-4h7.213L32 26l3 8h7l-6 4 1.938 7z"
                              />
                            </svg>
                          </div>
                          <span
                            className={`font-semibold text-sm ${
                              !isNewService ? "text-blue-700" : "text-gray-700"
                            }`}
                          >
                            Pagar Servicio
                          </span>
                          <span className="text-xs text-gray-500 mt-1">
                            Registrar pago existente
                          </span>
                          {!isNewService && (
                            <div className="absolute top-3 right-3">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="w-5 h-5 text-blue-500"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="mt-2">
                    <div className="mt-2">
                      <div className="">
                        <div
                          className={`flex flex-col ${
                            editModal?.id == undefined && "mt-5"
                          }`}
                        >
                          {isNewService && (
                            <div
                              className={`flex flex-col ${
                                editModal?.id == undefined && "mt-5"
                              }`}
                            >
                              {isNewService &&
                              editModal?.invoice === undefined ? (
                                <div className="mb-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Servicio{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    id="service"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                    placeholder="Ej: Limpieza dental, Ortodoncia..."
                                    required
                                    defaultValue={
                                      editModal?.id !== undefined
                                        ? editModal?.reason
                                        : ""
                                    }
                                    value={paymentReason}
                                    onChange={(e) =>
                                      setPaymentReason(e.target.value)
                                    }
                                  />
                                </div>
                              ) : (
                                <div className="mb-4">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Número de Factura
                                  </label>
                                  <input
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Ej: FAC-001234"
                                    required
                                    value={invoice}
                                    defaultValue={
                                      editModal?.id !== undefined &&
                                      editModal.invoice
                                    }
                                    onChange={(e) => setInvoice(e.target.value)}
                                  />
                                </div>
                              )}
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Monto <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                                    $
                                  </span>
                                  <input
                                    type="text"
                                    id="amount"
                                    className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors font-semibold text-gray-900"
                                    placeholder="0"
                                    defaultValue={
                                      editModal?.id !== undefined
                                        ? editModal?.price !== undefined
                                          ? editModal?.price
                                              .toString()
                                              .replace(
                                                /\B(?=(\d{3})+(?!\d))/g,
                                                ",",
                                              )
                                          : editModal?.amount
                                              .toString()
                                              .replace(
                                                /\B(?=(\d{3})+(?!\d))/g,
                                                ",",
                                              )
                                        : ""
                                    }
                                    value={
                                      paymentAmount
                                        ? paymentAmount
                                            .toString()
                                            .replace(
                                              /\B(?=(\d{3})+(?!\d))/g,
                                              ",",
                                            )
                                        : ""
                                    }
                                    onChange={handleChange}
                                  />
                                </div>
                              </div>
                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Descripción
                                </label>
                                <textarea
                                  rows="3"
                                  id="service"
                                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors resize-none"
                                  placeholder="Agrega detalles adicionales (opcional)"
                                  value={paymentDescription}
                                  defaultValue={
                                    editModal?.id !== undefined
                                      ? editModal?.description
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setPaymentDescription(e.target.value)
                                  }
                                />
                              </div>

                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Fecha del Servicio
                                </label>
                                <DatePicker
                                  selected={
                                    editModal?.date !== undefined
                                      ? typeof editModal?.date == "string"
                                        ? new Date(editModal?.date)
                                        : editModal?.date.toDate()
                                      : paymentDate
                                  }
                                  onChange={(date) => setPaymentDate(date)}
                                  className="w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pl-10 py-2.5 transition-colors"
                                  popperPlacement="top"
                                  showIcon
                                  dateFormat={"dd/MM/yyyy"}
                                  icon={DatePickerIcon}
                                />
                              </div>
                            </div>
                          )}

                          {editModal?.id === undefined &&
                            !isNewService &&
                            defaultValue &&
                            defaultValue.length == 0 && (
                              <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg text-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className="w-12 h-12 mx-auto text-blue-500 mb-3"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
                                  />
                                </svg>
                                <p className="text-sm font-medium text-blue-800">
                                  No hay servicios activos
                                </p>
                                <p className="text-xs text-blue-600 mt-1">
                                  Este paciente no tiene servicios pendientes de
                                  pago
                                </p>
                              </div>
                            )}

                          {!isNewService && data && dropSelection && (
                            <div className="flex flex-col space-y-4">
                              {editModal?.id === undefined && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Servicio a Pagar
                                      </label>
                                      <PaymentDropdown
                                        data={allPayments}
                                        currentValueSetter={setDropSelection}
                                        defaultValue={defaultValue}
                                      />
                                    </div>
                                    {dropSelection !== null && (
                                      <div className="flex flex-col justify-end">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                          Saldo Pendiente
                                        </label>
                                        <div className="bg-white px-4 py-2.5 rounded-lg border border-gray-300 font-semibold text-gray-900">
                                          $
                                          {currentDebt
                                            ? currentDebt
                                                .toString()
                                                .replace(
                                                  /\B(?=(\d{3})+(?!\d))/g,
                                                  ",",
                                                )
                                            : "0"}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Monto{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                                      $
                                    </span>
                                    <input
                                      className="w-full pl-8 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors font-semibold"
                                      placeholder="0"
                                      required
                                      defaultValue={
                                        editModal?.id !== undefined
                                          ? editModal?.price !== undefined
                                            ? editModal?.price
                                                .toString()
                                                .replace(
                                                  /\B(?=(\d{3})+(?!\d))/g,
                                                  ",",
                                                )
                                            : editModal?.amount
                                                .toString()
                                                .replace(
                                                  /\B(?=(\d{3})+(?!\d))/g,
                                                  ",",
                                                )
                                          : ""
                                      }
                                      value={
                                        paymentAmount
                                          ? paymentAmount
                                              .toString()
                                              .replace(
                                                /\B(?=(\d{3})+(?!\d))/g,
                                                ",",
                                              )
                                          : ""
                                      }
                                      onChange={handleChange}
                                      type="text"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Número de Factura{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                    placeholder="Ej: FAC-001234"
                                    required
                                    value={invoice}
                                    onChange={(e) => setInvoice(e.target.value)}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Descripción
                                </label>
                                <textarea
                                  rows="3"
                                  id="service"
                                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                                  placeholder="Agrega detalles adicionales (opcional)"
                                  value={paymentDescription}
                                  defaultValue={
                                    editModal?.id !== undefined
                                      ? editModal?.description
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setPaymentDescription(e.target.value)
                                  }
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Fecha del Pago
                                </label>
                                <DatePicker
                                  selected={paymentDate}
                                  onChange={(date) => setPaymentDate(date)}
                                  className="w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pl-10 py-2.5 transition-colors"
                                  popperPlacement="top"
                                  showIcon
                                  icon={DatePickerIcon}
                                  dateFormat={"dd/MM/yyyy"}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                  <button
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                    type="button"
                    onClick={() => modalSetter(false)}
                    ref={cancelButtonRef}
                  >
                    Cancelar
                  </button>
                  <button
                    className={`px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all duration-200 flex items-center gap-2 ${
                      !isFormValid() || serviceIsRunning
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 shadow-sm hover:shadow-md"
                    }`}
                    type="button"
                    onClick={updatePaymentHandler}
                    disabled={serviceIsRunning || !isFormValid()}
                  >
                    {!serviceIsRunning ? (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                          />
                        </svg>
                        Guardar
                      </>
                    ) : (
                      <>
                        <Spinner size="small" fill="fill-white" />
                        Guardando...
                      </>
                    )}
                  </button>
                </div>
              </>
            }
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
};

export default PaymentModal;
